// Read an app's deploy metadata from the package.json inside an uploaded zip bundle.
//
// The Console deploy flow requires the zip to contain a package.json at its root (mirrors the CLI's
// source of truth): its top-level `name`/`version` and the `atlas` manifest block drive the
// POST /apps request. We extract + parse it in the browser so the backend register contract is
// unchanged (name/version/manifest still arrive as request fields, not parsed server-side).
import { unzipSync, strFromU8 } from "fflate";

export interface ZipManifest {
  name: string;
  version: string;
  manifest: Record<string, unknown>;
  entries: string[];
}

// Parse the zip bytes (Uint8Array) and return { name, version, manifest, entries }.
// Throws a user-facing Error if the zip is invalid, has no package.json, or it's missing fields.
export function readZipManifest(bytes: Uint8Array): ZipManifest {
  let files: Record<string, Uint8Array>;
  try {
    files = unzipSync(bytes);
  } catch (e) {
    throw new Error(`not a valid zip file: ${(e as Error).message}`);
  }
  const entries = Object.keys(files);

  // The bundle must be flat, with files at the zip root rather than inside an output directory.
  // nested under a wrapper folder. The extractor preserves paths verbatim, so a "myapp/index.html"
  // would serve at /<app>/myapp/index.html (a 403 at /<app>/). This matches the CLI, which tars the
  // *contents* of public/. So package.json must be exactly at the root.
  if (!files["package.json"]) {
    throw new Error(
      "zip has no package.json at its root. Zip the contents of your app folder " +
        "(index.html, package.json, …), not the folder itself",
    );
  }

  let pkg: { name?: unknown; version?: unknown; atlas?: unknown };
  try {
    pkg = JSON.parse(strFromU8(files["package.json"]));
  } catch (e) {
    throw new Error(`package.json is not valid JSON: ${(e as Error).message}`);
  }
  if (!pkg.name || !pkg.version) {
    throw new Error("package.json must set both 'name' and 'version'");
  }
  return {
    name: String(pkg.name),
    version: String(pkg.version),
    manifest: (pkg.atlas as Record<string, unknown>) || {},
    entries,
  };
}

// True if the archive has a root index.html. This client-side guard mirrors the backend.
// fast before uploading. Root-only (see the flat-bundle requirement above).
export function hasIndexHtml(entries: string[]): boolean {
  return entries.includes("index.html");
}
